import type { ColumnFiltersState, RowData, RowSelectionState, SortingState } from "@tanstack/react-table";
import { toTitleCase } from "../../utils/formatText";
import { resolveServerId } from "../../modules/merchandising/utils/metrics";
import { clearActiveDataViewId } from "../../modules/dataViews/hooks/useDataView";
import type { ServerColumnFilter } from "../../zodios/common/columnFilterSchema";
import type { TSelector } from "../../i18n/types";
import { type AppColumn, type AppTable } from "./tableFeatures";

export function clientToServerFiltering(
  filters: ColumnFiltersState | undefined,
  extraFilters?: ServerColumnFilter[],
): ServerColumnFilter[] | undefined {
  if (!filters?.length && !extraFilters?.length) {
    return undefined;
  }

  const mapped: ServerColumnFilter[] = (filters ?? []).map((filter) => ({
    id: resolveServerId(filter.id),
    value: filter.value as ServerColumnFilter["value"],
  }));

  if (extraFilters?.length) {
    mapped.push(...extraFilters);
  }

  return mapped;
}

export function clientToServerSorting(sorting: SortingState): string | undefined {
  if (sorting.length === 0) {
    return undefined;
  }

  return sorting.map(({ id, desc }) => `${desc ? "-" : ""}${resolveServerId(id)}`).join(",");
}

export function resolveLabel<TData extends RowData, TValue = unknown>(
  column: AppColumn<TData, TValue>,
  metaKey?: "columnSelectorOptions" | "columnFilterOptions",
  t?: (selector: TSelector) => string,
): string {
  const printableHeader =
    typeof column.columnDef.header === "string" ? column.columnDef.header : toTitleCase(column.id);

  if (!metaKey) {
    return printableHeader;
  }

  const label = column.columnDef.meta?.[metaKey]?.label;
  if (typeof label === "string") return label;
  if (typeof label === "function" && t) return t(label);
  return printableHeader;
}

export function selectionStateToSelectedIds(state: RowSelectionState): string[] {
  // v9 removes deselected rows from the record, so every key is a selection.
  return Object.keys(state);
}

export function selectedIdsToSelectionState(ids: ReadonlyArray<string | number> | null | undefined): RowSelectionState {
  // Row ids are always strings, so numeric ids are stringified to match `getRowId`.
  return Object.fromEntries((ids ?? []).map((id) => [String(id), true]));
}

/**
 * This should just be table.reset(), but that's bugged in the current version of react-table.
 */
export function resetTable<TData extends RowData>(table: AppTable<TData>, viewKey?: string) {
  table.resetColumnFilters();
  table.resetColumnOrder();
  table.resetColumnVisibility();
  table.resetExpanded();
  table.resetGlobalFilter();
  table.resetPageIndex();
  table.resetPageSize();
  table.resetRowSelection();
  table.resetSorting();
  table.resetColumnSizing();
  if (viewKey) clearActiveDataViewId(viewKey);
}

export const idsToVisibilityState = (
  defaultSelectedColumnIds: string[],
  allColumnIds: string[],
): Record<string, boolean> => {
  const selectedIdsSet = new Set(defaultSelectedColumnIds);
  const defaultHiddenCols: Record<string, boolean> = {};

  allColumnIds.forEach((colId) => {
    if (colId && !selectedIdsSet.has(colId)) {
      defaultHiddenCols[colId] = false;
    }
  });

  return defaultHiddenCols;
};
